import hotkeys from "hotkeys-js";
import { Direction, HotkeyScopes, Positions, TreeDefaultNodeIds } from "../utils/constants";
import { ProcessItemTypes } from "../utils/icons";
import { designDefaultProps, hfInstance, helper } from "./Cells";
import api from "./api";
import Tree from "./panel/Tree";
import { v4 as uuidv4 } from "uuid";
import { produce } from "immer";
import { RedZoneHelper } from "./helpers/RedZoneHelper";
import { stateHistory } from "./state-history";

let redZoneHelper = null;
const initialState = {
  allSheets: [],
  currentSheet: null,
  editSheetId: null,
  activeSheet: {
    id: null,
    editMode: false,
  },
  persistTimeout: null,
  visibleItemIds: [],
  showTrash: false,
  selectedTrashItems: [],
  sheetsHashTable: {},
  tree: {},
};
export default {
  namespaced: true,
  state: () => ({ ...initialState }),
  mutations: {
    REMOVE_SHEET(state, { rootState, sheetNames }) {
      rootState.cells.current.design = produce(rootState.cells.current.design, (draftDesign) => {
        sheetNames.forEach((sheetName) => {
          delete draftDesign[sheetName]; // Remove each sheet from design
        });
      });
    },
  },
  actions: {
    setSheetsHashTable({ commit, state, dispatch }) {
      state.sheetsHashTable = state.tree.getHashTable(state.tree.allData);
    },
    async loadSheets({ commit, state, dispatch, getters }, { designId }) {
      const { sheets, sequence_value, current_sheet_name, duplicate_sheet_sequence_value } = (
        await api.get(`sheets/${designId}`)
      ).data;
      state.tree = new Tree(sheets);

      const props = state.tree.getAllPropertiesInSubTree(
        sheets,
        ["id", "title"],
        (node) => node.type === ProcessItemTypes.Sheet
      );
      state.allSheets = props.map((prop, index) => ({
        sheetId: index,
        name: prop.title,
        id: prop.id,
      }));
      state.currentSheet = state.allSheets.find((sheet) => sheet.name === current_sheet_name) ?? state.allSheets[0];
      state.sequenceValue = sequence_value;
      state.duplicateSheetSequenceValue = duplicate_sheet_sequence_value;
      dispatch("setActiveSheet", { id: state.currentSheet.id, editMode: false });
      dispatch("calculateVisibleItemIds");
      dispatch("setSheetsHashTable");
      redZoneHelper = new RedZoneHelper({ state, dispatch, getters });
    },
    toggleTrash({ commit, state, dispatch }, value) {
      if (value === undefined) value = !state.showTrash;
      state.showTrash = value;
    },
    setSelectedTrashItems({ commit, state, dispatch }, value) {
      state.selectedTrashItems = [...value];
    },
    async persist({ commit, state, rootState, dispatch }, teamIds = null) {
      const designId = rootState.cells.current.id;

      clearTimeout(state.persistTimeout);
      state.persistTimeout = setTimeout(async () => {
        try {
          api.patch(`sheets/${designId}`, { sheets: state.tree.allData });
        } catch (error) {
          console.log(error);
        }
      }, 500);
    },
    async duplicateSheet({ state, rootState, rootGetters, dispatch }, id) {
      try {
        const designId = rootState.cells.current.id;
        const regex = /(.+?)\((\d+)\)/;
        const sourceSheet = state.allSheets.find((x) => x.id === id);
        const sourceSheetName = sourceSheet.name;
        const match = sourceSheetName.match(regex) || [];

        state.duplicateSequence = match[2] || 1;
        const sheetName = match[1] ? match[1] : sourceSheetName;

        let name;
        do {
          const copyRegex = /(Copy)(\d+)?$/;
          // Append "Copy" or update name with the sequence number
          name = copyRegex.test(sheetName)
            ? sheetName.replace(copyRegex, `Copy${++state.duplicateSequence}`)
            : (name = `${sheetName}Copy${++state.duplicateSequence}`);
        } while (state.allSheets.some((x) => x.name.toLowerCase() === name.toLowerCase()));

        const newSheet = hfInstance.addSheet(name);
        const sheetId = hfInstance.getSheetId(newSheet);

        const sheet = {
          id: uuidv4(),
          sheetId,
          name,
        };

        const sourceSheetData = hfInstance.getSheetValues(sourceSheet.sheetId);

        try {
          hfInstance.setSheetContent(sheetId, sourceSheetData);
        } catch (e) {
          hfInstance.removeSheet(sheet.sheetId);
          dispatch(
            "addToast",
            {
              severity: "error",
              life: 3000,
              summary: "Failed to duplicate sheet",
              detail: "There is an invalid range reference",
            },
            { root: true }
          );
          return;
        }
        await dispatch("cells/resetFormulaRange", null, { root: true });
        state.currentSheet = sheet;
        state.allSheets = produce(state.allSheets, (allSheets) => {
          allSheets.push(sheet);
          return allSheets;
        });

        const node = state.tree.getNode(state.tree.allData, id);
        const parentNodeId = node.parents[node.parents.length - 1];
        state.tree.addNode(
          {
            id: sheet.id,
            title: name,
            type: ProcessItemTypes.Sheet,
            expanded: true,
            children: [],
          },
          parentNodeId
        );

        await api.patch(`designs/${designId}/duplicate-sheet`, {
          prevSheet: { prevSheetId: sourceSheet.id, prevSheetName: sourceSheetName },
          newSheet: { newSheetId: sheet.id, sheetName: name },
          duplicateSheetSequenceValue: ++state.duplicateSheetSequenceValue,
        });
        dispatch("setActiveSheet", { id: sheet.id, editMode: true });
        dispatch("calculateVisibleItemIds");
        await api.post("sheets", {
          sheets: state.tree.allData,
          designId,
          sequenceValue: state.sequenceValue,
          duplicateSheetSequenceValue: state.duplicateSheetSequenceValue,
        });

        dispatch("cells/calculateRowMap", null, { root: true });
        dispatch("cells/calculateColumnMap", null, { root: true });
        await dispatch("views/load", designId, { root: true });
        dispatch("cells/hideNavigationCorner", null, { root: true });
        helper.merge.utils.loadMergedCellIds();
        helper.range.utils.focusFirstVisibleCell();
        await api.patch(`sheets/${designId}/currentSheet`, { current_sheet_name: name });
        await dispatch("cells/load", designId, { root: true });
        dispatch("setSheetsHashTable");
        dispatch("smartSearch/loadSheets", null, { root: true });
      } catch (error) {
        console.log(error);
      }
    },
    async addSheet({ state, rootState, rootGetters, dispatch }, id) {
      if (state.isLoading) return;
      state.isLoading = true;
      try {
        const designId = rootState.cells.current.id;
        let name;
        do {
          name = `Sheet${++state.sequenceValue}`;
        } while (state.allSheets.some((x) => x.name.toLowerCase() === name.toLowerCase()));

        const newSheet = hfInstance.addSheet(name);
        const sheetId = hfInstance.getSheetId(newSheet);

        const sheet = {
          id: uuidv4(),
          sheetId,
          name,
        };
        const { rowMap, columnMap } = (await api.patch(`designs/${designId}/add-sheet`, { sheetName: name })).data;
        await api.patch(`sheets/${designId}/currentSheet`, { current_sheet_name: name });

        // get rows and columns from rowMap and columnMap.
        const { rows, columns } = rootGetters["cells/getRowsColumnsFromMaps"](rowMap, columnMap);

        await dispatch("cells/resetFormulaRange", null, { root: true });
        rootState.cells.current.design = { ...designDefaultProps, columns, rows };
        state.currentSheet = sheet;
        const allSheets = produce(state.allSheets, (allSheets) => {
          allSheets.push(sheet);
          return allSheets;
        });
        state.allSheets = allSheets;

        state.tree.addNode(
          {
            id: sheet.id,
            title: name,
            type: ProcessItemTypes.Sheet,
            expanded: true,
            children: [],
          },
          id
        );
        dispatch("setActiveSheet", { id: sheet.id, editMode: true });
        dispatch("calculateVisibleItemIds");
        await api.post("sheets", { sheets: state.tree.allData, designId, sequenceValue: state.sequenceValue });
        dispatch("rangeStyle/load", { data: null }, { root: true });
        dispatch("cells/calculateRowMap", null, { root: true });
        dispatch("cells/calculateColumnMap", null, { root: true });
        await dispatch("views/load", designId, { root: true });
        dispatch("cells/hideNavigationCorner", null, { root: true });
        helper.merge.utils.loadMergedCellIds();
        helper.range.utils.focusFirstVisibleCell();
        dispatch("setSheetsHashTable");
        dispatch("smartSearch/loadSheets", null, { root: true });
        dispatch("table/loadSheetRelated", "", { root: true });
        setTimeout(async () => {
          await dispatch("corners/loadTables", null, { root: true });
          await dispatch("corners/loadCorners", null, { root: true });
        }, 0);
        stateHistory.reset();
        helper.setProperties();
      } catch (error) {
        console.log(error);
      } finally {
        state.isLoading = false;
      }
    },
    async openSheet({ commit, state, rootState, rootGetters, dispatch }, sheetId) {
      const sheet = state.allSheets.find((item) => item.id === sheetId);
      if (
        !sheet ||
        state.isLoading ||
        sheet.id === state.currentSheet.id ||
        sheet.id === undefined ||
        sheet.type === TreeDefaultNodeIds.Sheets
      )
        return; // do not open current sheet or parent level tree.
      state.isLoading = true;
      try {
        const designId = rootState.cells.current.id;
        await dispatch("cells/resetFormulaRange", null, { root: true });
        dispatch("resetActiveContextMenu", null, { root: true });

        state.currentSheet = sheet;
        const design = (await api.get(`designs/${designId}?r=${Math.random()}`)).data.design[sheet.name];
        await api.patch(`sheets/${designId}/currentSheet`, { current_sheet_name: sheet.name });

        helper.setProperties();
        stateHistory.reset();
        // get rows and columns from rowMap and columnMap.
        const { rows, columns } = rootGetters["cells/getRowsColumnsFromMaps"](design.rowMap, design.columnMap);
        rootState.cells.current.design = { ...designDefaultProps, ...design, rows, columns };

        dispatch("cells/calculateRowMap", null, { root: true });
        dispatch("cells/calculateColumnMap", null, { root: true });
        await dispatch("views/load", designId, { root: true });
        dispatch("rangeStyle/load", { data: design.styleTree }, { root: true });
        dispatch("table/loadSheetRelated", "", { root: true });
        dispatch("table/loadRTree", null, { root: true });

        setTimeout(() => {
          (async () => {
            try {
              await dispatch("corners/loadTables", null, { root: true });
              await dispatch("corners/loadCorners", null, { root: true });
              await dispatch("satelliteCorner/load", null, { root: true });
              await dispatch("styleCorner/startStyles", null, { root: true });
            } catch (error) {
              console.error("Error during async dispatches:", error);
            }
          })();
        }, 0);
        dispatch("cells/hideNavigationCorner", null, { root: true });
        helper.merge.utils.loadMergedCellIds();
        helper.range.utils.focusFirstVisibleCell();
        dispatch("setScope", HotkeyScopes.ProcessDesigner, { root: true }); // for toggling active scope. ctrl+\
      } catch (error) {
        console.log(error);
      } finally {
        state.isLoading = false;
      }
    },
    async moveToTrash({ commit, state, rootState, getters, dispatch }, id) {
      try {
        if (!state.visibleItemIds.includes(id)) return;

        const current = rootState.cells.current;
        const gridNode = state.tree?.getNodeByItemId(state.tree.allData, current.id);
        if (gridNode.children.length === 1 && gridNode.children[0].id === id)
          return dispatch(
            "addToast",
            {
              severity: "error",
              life: 3000,
              summary: "Failed To Delete",
              detail: "A grid must contain at least one visible sheet",
            },
            { root: true }
          );

        const node = getters.getSheetById(id);
        node.reference = node.reference || [...node.parents];

        // Generate the path using the reference or parents
        const pathParts = node.parents
          .map((parentId) => {
            const parentNode = state.tree.getNode(state.tree.allData, parentId);
            return parentNode?.title || ""; // Use title if available
          })
          .filter((title) => title.toLowerCase() !== "root"); // Remove "root" from the path

        node.path = pathParts; // Construct the path as a string

        const subTrashNodeId = `TRASH_${current.id}`;
        const subTrashNode = state.tree.getNode(state.tree.allData, subTrashNodeId);
        if (!subTrashNode) {
          state.tree.addNode(
            {
              id: `TRASH_${current.id}`,
              title: current.title,
              expanded: false,
              type: ProcessItemTypes.Folder,
              children: [],
            },
            TreeDefaultNodeIds.Trash
          );
        }

        // if current sheet is child of the deleted sheet
        const isChild = state.tree.isChildOf(node, state.currentSheet.id);
        let sheet = state.currentSheet;
        if (id === state.currentSheet.id || isChild) {
          const parentNodeId = node.parents[node.parents.length - 1];
          const parentNode = state.tree.getNode(state.tree.allData, parentNodeId);
          if (parentNode.children.length > 1) {
            const deletedItemIndex = parentNode.children.findIndex((child) => child.id === id);
            const newNode = parentNode.children[deletedItemIndex + 1] || parentNode.children[deletedItemIndex - 1];
            sheet = state.allSheets.find((x) => x.id === newNode.id);
            dispatch("openSheet", sheet.id);
          } else {
            sheet = state.allSheets.find((x) => x.id === parentNode.id);
            dispatch("openSheet", sheet.id);
          }
          const designId = rootState.cells.current.id;
          await api.patch(`sheets/${designId}/currentSheet`, { current_sheet_name: sheet.name });
        }

        state.tree.removeNode(id);
        state.tree.addNode(node, subTrashNodeId);

        dispatch("calculateVisibleItemIds");
        dispatch("setActiveSheet", { id: sheet.id });
        dispatch("setSheetsHashTable");
        dispatch("persist");
        dispatch("smartSearch/loadSheets", null, { root: true });

        state.isLoading = false;
      } catch (error) {
        dispatch(
          "addToast",
          {
            severity: "error",
            life: 3000,
            summary: "Failed To Delete",
            detail: "Sorry Something went wrong",
          },
          { root: true }
        );
        console.log(error);
      }
    },
    async remove({ commit, state, rootState, getters, dispatch }, { ids }) {
      try {
        const designId = rootState.cells.current.id;
        let allSheets = [...state.allSheets];
        const allTitles = [];
        let allSheetIds = [];
        ids.forEach((trashId) => {
          state.isLoading = true;
          const node = getters.getSheetById(trashId);

          const props = state.tree.getAllPropertiesInSubTree(node, ["id", "title"]).reduce(
            (acc, obj) => {
              acc.ids.push(obj.id);
              acc.titles.push(obj.title);
              return acc;
            },
            { ids: [], titles: [] }
          );
          allTitles.push(...props.titles);
          props.ids.reverse().forEach((id) => {
            const node = { ...state.tree.getNode(state.tree.allData, id) };
            node.reference = node.reference || [...node.parents];
            state.tree.removeNode(id);

            // remove empty trash can
            const trashProcessNode = state.tree.getNode(state.tree.allData, node.parents[2]);
            if (trashProcessNode?.children.length === 0) state.tree.removeNode(trashProcessNode.id);

            const sheet = allSheets.find((x) => x.id === id);
            allSheetIds.push(sheet.id);
            hfInstance.removeSheet(sheet.sheetId);
            allSheets = allSheets.filter((x) => x.id !== id);
          });
        });
        state.allSheets = allSheets;

        commit("REMOVE_SHEET", { rootState, sheetNames: allTitles });
        await api.delete(`designs/${designId}/delete-sheet`, { params: { sheetNames: allTitles } });
        await api.delete(`views/${designId}/delete-sheet`, { params: { sheetIds: ids } });
        //delete all tables in sheet
        await dispatch("table/deleteTablesInSheet", { sheetIds: allSheetIds }, { root: true });
        await dispatch("table/deleteNamedRangesInSheet", { sheetIds: allSheetIds }, { root: true });
        setTimeout(() => {
          dispatch("corners/loadTables", null, { root: true });
          dispatch("corners/loadCorners", null, { root: true });
        }, 0);
        dispatch("persist");

        dispatch(
          "addToast",
          {
            severity: "success",
            life: 3000,
            summary: "Deleted Successfully",
            detail: "Your data has been deleted permanently.",
          },
          { root: true }
        );
        await dispatch("setSheetsHashTable");
      } catch (error) {
        dispatch(
          "addToast",
          {
            severity: "error",
            life: 3000,
            summary: "Failed To Delete",
            detail: "Sorry Something went wrong",
          },
          { root: true }
        );
        console.log(error);
      } finally {
        state.isLoading = false;
      }
    },
    restore({ commit, state, dispatch }, { ids }) {
      ids.forEach((id) => {
        const node = state.tree.getNode(state.tree.allData, id);
        const trashProcessId = node.parents[2];
        const lastParentId = node.reference[node.reference.length - 1];
        const lastParent = state.tree.getNode(state.tree.allData, lastParentId);

        const returnPoint =
          lastParent && lastParent.parents[1] !== TreeDefaultNodeIds.Trash ? lastParentId : node.reference[1];

        state.tree.setValue(id, "reference", null);
        state.tree.moveNode(id, Positions.Center, returnPoint);

        // remove empty trash can
        const trashProcessNode = state.tree.getNode(state.tree.allData, trashProcessId);

        if (trashProcessNode?.children.length === 0) {
          state.tree.removeNode(trashProcessId);
        }
      });
      dispatch("calculateVisibleItemIds");
      dispatch("persist");
      dispatch("setSheetsHashTable");
      dispatch("smartSearch/loadSheets", null, { root: true });
    },
    async renameSheet({ state, rootState, dispatch }, { id, sheetId, newSheetName }) {
      try {
        state.tree.setValue(id, "title", newSheetName);

        const { allSheets, currentSheet } = { ...state };
        const current = rootState.cells.current;

        // sheetId is sheetIndex. (it is necessary for hyperformula)
        const prevSheetName = allSheets[sheetId]?.name;

        allSheets[sheetId].name = newSheetName;

        state.currentSheet = allSheets.find((x) => x.sheetId === currentSheet.sheetId);
        state.allSheets = allSheets;

        hfInstance.renameSheet(sheetId, newSheetName);

        await Promise.all([api.patch(`designs/${current.id}/rename-sheet`, { prevSheetName, newSheetName })]);
        await dispatch("setSheetsHashTable");
        const sheet = allSheets.find((x) => x.sheetId === sheetId);
        dispatch("persist");
        dispatch("setActiveSheet", { id, editMode: false });
        dispatch("smartSearch/loadSheets", null, { root: true });
        dispatch("table/updateNamedExpressionInSheet", sheet.id, { root: true });
        helper.setProperties();

        state.isLoading = false;
      } catch (err) {
        console.log(err);
      }
    },
    setActiveSheet({ commit, state, dispatch }, { id, editMode = false }) {
      if (editMode) dispatch("resetActiveContextMenu", null, { root: true });
      state.activeSheet = {
        id,
        editMode,
      };
    },
    resetActiveSheet({ commit, state, dispatch }) {
      state.activeSheet = {
        id: null,
        editMode: false,
      };
    },
    toggleExpanded({ commit, state, dispatch }, { id, expanded = null }) {
      const item = state.tree.getNode(state.tree.allData, id);
      if (!item) return;

      if (expanded === null) expanded = !item.expanded;

      state.tree.setValue(id, "expanded", expanded);

      dispatch("setActiveSheet", { id });
      dispatch("calculateVisibleItemIds");
      dispatch("resetActiveContextMenu", null, { root: true });
      dispatch("setSheetsHashTable");
    },
    calculateVisibleItemIds({ commit, state, dispatch }) {
      state.visibleItemIds = state.tree.getVisibleNodeIds(state.tree.activeSubTree);
    },
    setEditSheetId({ commit, state, dispatch }, value) {
      state.editSheetId = value;
    },
    moveNode(_, { id, direction }) {
      redZoneHelper.moveNode({ id, direction });
    },
    moveNodeOneLevel(_, { id, direction }) {
      redZoneHelper.moveNodeOneLevel({ id, direction });
    },
    moveActiveItem({ commit, getters, state, dispatch }, direction) {
      if (!state.activeSheet.id) {
        const id = state.visibleItemIds[0];
        return dispatch("setActiveSheet", { id });
      }

      let index = state.visibleItemIds.findIndex((item) => item === state.activeSheet.id);
      if (direction === Direction.Up) index = index > 0 ? index - 1 : 0;
      else index = index < state.visibleItemIds.length - 1 ? index + 1 : index;

      const newElementId = state.visibleItemIds[index];
      const element = document.querySelector(`.kk-options__tree [data-id="${newElementId}"]`);
      element.scrollIntoView({ block: "center" });

      const newActiveSheetId = state.visibleItemIds[index];
      dispatch("setActiveSheet", { id: newActiveSheetId });
    },
    moveSheet({ commit, state, dispatch, getters }, { id, position, targetId }) {
      if (!getters.isMoveAllowed(id, targetId)) return false;

      state.tree.moveNode(id, position, targetId);

      dispatch("calculateVisibleItemIds");
      dispatch("persist");
    },
    async resetState({ state }) {
      Object.assign(state, { ...initialState });
    },
  },
  getters: {
    getHfIndex: (state) => (id) => {
      return state.allSheets.findIndex((x) => x.id === id);
    },
    isEditAllowed: (state) => (id) => {
      const node = state.tree.getNode(state.tree.allData, id);

      if (node.parents.length === 1) return false;
      return true;
    },
    isMoveDownAllowed: (state, getters) => (id) => {
      const node = state.tree.getNode(state.tree.allData, id);

      if (node.parents.length === 1 || node.parents[1] === TreeDefaultNodeIds.Trash || getters.subTreeParents(id) === 0)
        return false;
      return true;
    },
    isMoveUpAllowed: (state, getters) => (id) => {
      const node = state.tree.getNode(state.tree.allData, id);
      // Take into account the active subtree. the first two levels in sub tree are not allowed to move up
      if (
        node.parents.length <= 2 ||
        node.parents[1] === TreeDefaultNodeIds.Trash ||
        getters.subTreeParents(id).length === 1
      )
        return false;
      return true;
    },
    // parents of node with regards to active sub tree
    subTreeParents: (state) => (id) => {
      const node = state.tree.getNode(state.tree.allData, id);

      const index = node.parents.indexOf(state.tree.activeSubTree.id);
      const subTreeParents = index !== -1 ? node.parents.slice() : [];

      return subTreeParents;
    },
    getSheetById: (state) => (id) => {
      return state.tree?.getNode(state.tree.allData, id);
    },
    getTrashItems: (state, getters) => () => {
      const items = [];
      const trash = getters.getSheetById(TreeDefaultNodeIds.Trash);
      trash?.children.forEach((item) => {
        items.push(...item.children);
      });

      return items;
    },
    // used when item is dragged and dropped
    isMoveAllowed: (state) => (id, targetId) => {
      const node = state.tree.getNode(state.tree.allData, id);
      const target = state.tree.getNode(state.tree.allData, targetId);

      if (node.parents[1] !== target.parents[1]) return false;

      return true;
    },
  },
};
